// CRM analytics client factory.
//
// Phase 1 (per ADR-0002) is stdout-only: no `posthogKey` means the
// ClientBase buffers events in-memory and `debug: true` echoes them to the
// console. We still wire `getScope` so every emission carries the current
// workspace/user (via the Zustand store) once a user is logged in.
//
// Why `useScopeStore.getState()` and not `useCurrentScope()`?
//   - `getScope` is called outside React's render phase by the analytics
//     client. Calling a Zustand hook there would violate rules-of-hooks.
//   - `getState()` is the documented non-React accessor — Zustand stores
//     notify subscribers, but an ad-hoc read is safe and synchronous.
//
// TODO [PHASE-2]: plug posthog-js when VITE_POSTHOG_KEY is set.

import type { AnalyticsClient, AnalyticsConfig, Scope as AnalyticsScope } from '@govard/analytics';
import { createClientAnalytics } from '@govard/analytics';

import { useScopeStore } from '../shared/scope/useCurrentScope.js';

function readScope(): AnalyticsScope | null {
  const state = useScopeStore.getState();
  if (state.user === null || state.scope === null) {
    return null;
  }
  const payload: AnalyticsScope = {
    workspaceId: state.scope.workspaceId,
    userId: state.user.id,
  };
  if (typeof state.scope.isBillable === 'boolean') {
    payload.isBillable = state.scope.isBillable;
  }
  return payload;
}

export function createAnalytics(): AnalyticsClient {
  const rawKey = import.meta.env.VITE_POSTHOG_KEY ?? '';
  // Build the config explicitly so `exactOptionalPropertyTypes` doesn't flag
  // an `undefined` literal against an optional-but-not-undefined-allowed field.
  const config: AnalyticsConfig = {
    getScope: readScope,
    anonymizeIp: true,
    debug: import.meta.env.DEV,
  };
  if (rawKey.length > 0) {
    config.posthogKey = rawKey;
  }
  return createClientAnalytics(config);
}
